import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { 
  Image, Upload, Trash2, Loader2, Search, Copy, 
  FileImage, RefreshCw, Eye, X, Check
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function MediaManager({ adminKey }) {
  const { t } = useLanguage();
  const [medias, setMedias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const fileInputRef = useRef(null);

  const fetchMedias = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/medias`, {
        headers: { 'X-Admin-Key': adminKey }
      });
      const data = await response.json();
      setMedias(data.medias || []);
    } catch (error) {
      toast.error(t('admin.load_error'));
    } finally {
      setLoading(false);
    }
  }, [adminKey, t]);

  useEffect(() => {
    fetchMedias();
  }, [fetchMedias]);

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    const uploadPromises = [];

    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('alt_text', file.name.split('.')[0]);

      uploadPromises.push(
        fetch(`${API_URL}/api/admin/medias/upload`, {
          method: 'POST',
          headers: { 'X-Admin-Key': adminKey },
          body: formData
        }).then(r => r.json())
      );
    }

    try {
      const results = await Promise.all(uploadPromises);
      const successCount = results.filter(r => r.media_id).length;
      toast.success(`${successCount} ${t('admin.files_uploaded')}`);
      fetchMedias();
    } catch (error) {
      toast.error(t('admin.upload_error'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async (mediaId) => {
    if (!window.confirm(t('admin.delete') + '?')) return;

    try {
      await fetch(`${API_URL}/api/admin/medias/${mediaId}`, {
        method: 'DELETE',
        headers: { 'X-Admin-Key': adminKey }
      });
      toast.success(t('admin.media_deleted'));
      setMedias(prev => prev.filter(m => m.media_id !== mediaId));
      if (selectedMedia?.media_id === mediaId) {
        setSelectedMedia(null);
        setPreviewDialog(false);
      }
    } catch (error) {
      toast.error(t('admin.delete_error'));
    }
  };

  const copyUrl = (url) => {
    const fullUrl = url.startsWith('/') ? `${API_URL}${url}` : url;
    navigator.clipboard.writeText(fullUrl);
    toast.success(t('admin.url_copied'));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const filteredMedias = medias.filter(m => 
    m.original_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.alt_text?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const imageMedias = filteredMedias.filter(m => m.content_type?.startsWith('image/'));

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold">{t('admin.media_library')}</h2>
          <p className="text-muted-foreground">{medias.length} {t('admin.files_count')} • {t('admin.upload_manage_images')}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchMedias} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            {t('admin.refresh')}
          </Button>
          <Button onClick={() => fileInputRef.current?.click()} disabled={uploading}>
            {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
            {t('admin.upload')}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            className="hidden"
            onChange={handleUpload}
          />
        </div>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input 
          placeholder={t('admin.search_media')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Media Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      ) : imageMedias.length === 0 ? (
        <Card>
          <CardContent className="py-20 text-center">
            <FileImage className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground mb-4">
              {searchQuery ? t('admin.no_media_found') : t('admin.no_media_uploaded')}
            </p>
            <Button onClick={() => fileInputRef.current?.click()}>
              <Upload className="h-4 w-4 mr-2" />
              {t('admin.upload_first_image')}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {imageMedias.map(media => (
            <Card 
              key={media.media_id}
              className="group cursor-pointer overflow-hidden hover:shadow-lg transition-all"
              onClick={() => { setSelectedMedia(media); setPreviewDialog(true); }}
            >
              <div className="aspect-square relative bg-muted">
                <img 
                  src={`${API_URL}${media.url}`}
                  alt={media.alt_text || media.original_name}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <Button size="sm" variant="secondary" className="h-8">
                    <Eye className="h-4 w-4" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="destructive" 
                    className="h-8"
                    onClick={(e) => { e.stopPropagation(); handleDelete(media.media_id); }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <CardContent className="p-2">
                <p className="text-xs font-medium truncate">{media.original_name}</p>
                <p className="text-xs text-muted-foreground">{formatFileSize(media.size)}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Preview Dialog */}
      <Dialog open={previewDialog} onOpenChange={setPreviewDialog}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{t('admin.media_details')}</DialogTitle>
          </DialogHeader>
          {selectedMedia && (
            <div className="grid md:grid-cols-2 gap-6">
              {/* Image Preview */}
              <div className="bg-muted rounded-lg overflow-hidden">
                <img 
                  src={`${API_URL}${selectedMedia.url}`}
                  alt={selectedMedia.alt_text || selectedMedia.original_name}
                  className="w-full h-auto"
                />
              </div>

              {/* Details */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>{t('admin.file_name')}</Label>
                  <p className="text-sm font-medium">{selectedMedia.original_name}</p>
                </div>

                <div className="space-y-2">
                  <Label>{t('admin.size')}</Label>
                  <p className="text-sm">{formatFileSize(selectedMedia.size)}</p>
                </div>

                <div className="space-y-2">
                  <Label>{t('admin.type')}</Label>
                  <p className="text-sm">{selectedMedia.content_type}</p>
                </div>

                <div className="space-y-2">
                  <Label>{t('admin.date_added')}</Label>
                  <p className="text-sm">
                    {new Date(selectedMedia.created_at).toLocaleDateString('fr-CA', {
                      year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
                    })}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>{t('admin.url')}</Label>
                  <div className="flex gap-2">
                    <Input 
                      value={`${API_URL}${selectedMedia.url}`}
                      readOnly
                      className="text-xs"
                    />
                    <Button size="sm" variant="outline" onClick={() => copyUrl(selectedMedia.url)}>
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button 
                    variant="destructive" 
                    className="flex-1"
                    onClick={() => handleDelete(selectedMedia.media_id)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    {t('admin.delete')}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
